import React from "react";
import styled from "styled-components";
import DeleteIcon from "../assets/icons/DeleteIcon";
import DeletePromptIcon from "../assets/icons/DeletePromptIcon";
import CloseWhiteIcon from "../assets/icons/CloseWhiteIcon";
import deletePromptBg from "../assets/images/deletePRomptBG.png";
import LoadingCircleIcon from "../assets/icons/LoadingCircleIcon";
import CrossIcon from "../assets/icons/CrossIcon";
import { useTheme } from "../middleware/ThemeContext";
import { Button } from "./form/Button";
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

export const Container = styled('div')(({ theme }) => ({
  width: '100%',
  maxWidth: '600px',
  background: theme.CorporateTheme.colors.background,
  borderRadius: '8px',
  overflow: 'hidden',
  boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.15)',
  textAlign: 'center',
  fontFamily: '"Segoe UI", sans-serif',
}));

export const Header = styled.div`
  background-image: url(${deletePromptBg});
  background-repeat: repeat;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 48px;
  background-size: cover;
  background-position: center;
`;

export const BigIconWrapper = styled.div`
  background-color: #fde6e6;
  padding: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const Body = styled.div`
  padding: 24px;
`;

export const Title = styled('h2')(({ theme }) => ({
  fontSize: '18px',
  fontWeight: 700,
  fontFamily: '"Raleway", sans-serif',
  color: `${theme.CreateFormTheme.colors.primary} !important`,
  marginBottom: '12px',
}));

export const Subtitle = styled('p')(({ theme }) => ({
  fontSize: '14px',
  color: theme.CorporateTheme.colors.textSecondary,
  fontWeight: 400,
  lineHeight: 1.5,
  fontFamily: '"Raleway", sans-serif',
}));

export const Actions = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
  padding: 24px;
`;

export const ActionButton = styled.button<{
  variant: "delete" | "cancel" | "submit";
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-size: 16px;
  padding: 7px 16px;
  border: 2px solid ${({ theme }) => theme.CreateFormTheme.colors.border};
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s ease;
  background-color: ${({ variant, theme }) =>
    variant === "delete" || variant === "submit"
      ? theme.CorporateTheme.colors.background
      : theme.CreateFormTheme.colors.primaryBtn};
  color: ${({ variant, theme }) =>
    variant === "delete" || variant === "submit" ? theme.CorporateTheme.colors.textSecondary : theme.CorporateTheme.colors.background};

  label {
    margin-top: 2px;
    font-family: "Raleway", sans-serif;
    font-weight: 500;
    font-size: 14px;
    cursor: pointer;
  }
  svg {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
    display: block;
  }

  &:hover {
    opacity: 0.9;
    background: ${({ variant, theme }) =>
    variant === "delete" || variant === "submit"
      ? theme.CreateFormTheme.colors.hoverCancel
      : theme.CreateFormTheme.colors.hoverPrimary};
  }
`;

export const DeleteIconStyled = styled(DeleteIcon)`
  width: 16px;
  height: 16px;
`;

export const CloseIconStyled = styled(CloseWhiteIcon)`
  width: 16px;
  height: 16px;
`;

interface DeletePromptProps {
  onConfirm: () => void;
  onCancel: () => void;
  title?: string;
  subTitle?: string;
  loading?: boolean;
}

const DeletePrompt: React.FC<DeletePromptProps> = ({
  onConfirm,
  onCancel,
  title,
  subTitle,
  loading,
}) => {
  const { theme } = useTheme();
  return (
    <Overlay>
      <Container theme={theme}>
        <Header>
          <BigIconWrapper>
            <DeletePromptIcon />
          </BigIconWrapper>
        </Header>
        <Body>
          <Title theme={theme}>
            {title ?? "Are you sure you want to delete this record?"}
          </Title>
          <Subtitle theme={theme}>
            {subTitle ??
              `Deleting this record
            will remove all associated data, including events, certifications,
            memberships, and more.`}
          </Subtitle>
        </Body>

        <Actions>
          <Button theme={theme} $variant="customButton" onClick={onCancel}>
            <CrossIcon />
            Cancel
          </Button>
          <Button theme={theme} onClick={onConfirm}>
            {loading ? (
              <>
                <label>Deleting...</label>
                <LoadingCircleIcon />
              </>
            ) : (
              <>
                <DeleteIcon size={24} />
                Delete Record
              </>
            )}
          </Button>
        </Actions>
      </Container>
    </Overlay>
  );
};
export default DeletePrompt;
